import React, { useCallback, useEffect, useState } from 'react';

import { Box, Grid } from '@material-ui/core';
import RichTextEditor from 'components/RichTextEditor';
import SkeletonLoader from 'components/SkeletonLoader';
import { StyledAvatar } from 'components/avatars';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { StyledGrid, StyledTypography, ImageSizesCSS, StyledBox } from 'styles/common';

import PeerFeedbackPlaceholderIcon from 'assets/images/customer-feedback.svg?react';

import { REVIEW_ANSWER_TYPE_MAP } from 'constants/ProjectConstants';
import theme from 'constants/theme';

import { getPeerFeedback } from 'services/performanceReview';
import { showErrorSnackbar } from 'services/snackbar';

import NumberRating from 'uiToolkit/NumberRating';
import StarRating from 'uiToolkit/StarRating';

import { useReview } from '../ReviewContext';
import DisplayInfo from './DisplayInfo';

const NO_DATA_MSG = 'No data to display';
const ERROR_MSG = 'Request to fetch peer feedback failed!';
const NO_FEEDBACK_YET = 'No Feedback yet.';

const ImageContainer = styled.div`
  width: 60%;
  svg {
    fill: ${(props) => props.theme.palette.text.secondaryLight};
    ${ImageSizesCSS.bigger};
  }
`;

const StyledTypography1 = styled(StyledTypography)`
  background: #bbb;
  padding: 2px 0.7rem;
  border-radius: 3px;
  margin-left: 0.8rem;
  font-size: 0.8rem;
`;

const Separator = styled.hr`
  border: 0;
  border-bottom: 1px solid #e1e1e1;
  margin: 1.5rem 0.5rem 1.4rem -0.4rem;
`;

const RatingComponent = ({ selectedAnswerOptions, answerType, answerOptions }) => {
  const getAnswerComponent = () => {
    switch (answerType) {
      case REVIEW_ANSWER_TYPE_MAP.STAR_RATING:
        return (
          <StyledBox
            flexcontainer
            justifycontent="flex-start"
            alignitems="flex-start"
            flexdirection="column"
            bottomGutter={2}
          >
            <StyledTypography variant="body2" weight={600} color="textSecondary" bottomGutter={1}>
              Score
            </StyledTypography>
            <StarRating
              readOnly
              max={answerOptions.length}
              prevVal={selectedAnswerOptions[0]?.displayOrder || 0}
              size="small"
            />
          </StyledBox>
        );
      case REVIEW_ANSWER_TYPE_MAP.NUMBER_RATING:
        return (
          <StyledBox
            flexcontainer
            justifycontent="flex-start"
            alignitems="flex-start"
            bottomGutter={2}
            flexdirection="column"
          >
            <StyledTypography variant="body2" weight={600} color="textSecondary" bottomGutter={1}>
              Score
            </StyledTypography>
            <NumberRating
              readOnly
              data={answerOptions}
              selectedId={selectedAnswerOptions[0]?.id || null}
              descriptionKey="score"
            />
          </StyledBox>
        );
      default:
        return selectedAnswerOptions.map(({ description }) => (
          <StyledBox bottomGutter={2}>
            <StyledTypography variant="body2" weight={600} color="textSecondary" bottomGutter={1}>
              Score
            </StyledTypography>
            <StyledTypography variant="body2" style={{ whiteSpace: 'pre-line' }}>
              {description}
            </StyledTypography>
          </StyledBox>
        ));
    }
  };
  return selectedAnswerOptions && selectedAnswerOptions.length ? getAnswerComponent() : null;
};

const FeedbackComponent = ({ feedbackText, feedbackTextHtml, answerType }) =>
  feedbackText && feedbackText.length ? (
    <StyledBox bottomGutter={2}>
      {answerType !== REVIEW_ANSWER_TYPE_MAP.NONE && (
        <StyledTypography variant="body2" weight={600} color="textSecondary" bottomGutter={1}>
          Comments/examples
        </StyledTypography>
      )}
      <RichTextEditor
        placeholder="-"
        initialText={feedbackTextHtml}
        plainText={feedbackText}
        showToolbar={false}
        readOnly
      />
    </StyledBox>
  ) : null;

const NoFeedbackComponent = () => (
  <StyledBox bottomGutter={2}>
    <StyledTypography variant="body2" color="red" bottomGutter={1}>
      {NO_FEEDBACK_YET}
    </StyledTypography>
  </StyledBox>
);

const PlaceHolderComp = () => (
  <Grid container xs={12} justify="center" align="center">
    <Box marginTop="4rem" marginBottom="3rem" width="66%">
      <ImageContainer>
        <PeerFeedbackPlaceholderIcon />
      </ImageContainer>
      <StyledTypography topGutter={3} variant="h6" color="textSecondaryLight">
        Select a question to view
        <br />
        self and peer feedback
      </StyledTypography>
    </Box>
  </Grid>
);

const FeedbackHeader = ({ employee, providerType }) => {
  const { displayName, designation, profilePhotoSrc } = employee;
  return (
    <StyledGrid container bottomGutter={2}>
      <StyledAvatar
        src={profilePhotoSrc}
        rightGutter={2}
        size="SmallerSizeCSS"
        variant="circular"
        border={`2px solid ${theme.palette.borders.lightGrey}`}
      />
      <Grid item direction="column">
        <StyledTypography variant="body1">
          {displayName}
          {providerType && (
            <StyledTypography1 variant="body2" display="inline" color="white" weight={600}>
              {providerType.toProperCase()}
            </StyledTypography1>
          )}
        </StyledTypography>
        <StyledTypography variant="caption" color="textSecondary">
          {designation}
        </StyledTypography>
      </Grid>
    </StyledGrid>
  );
};

const PeerFeedbackDisplayCard = ({
  employee,
  feedbackText,
  feedbackTextHtml,
  selectedAnswerOptions,
  answerType,
  providerType,
  isLastItem,
  answerOptions,
}) => {
  let someDataPresent = false;
  someDataPresent = someDataPresent || (selectedAnswerOptions && selectedAnswerOptions.length > 0);
  someDataPresent = someDataPresent || (feedbackText && feedbackText.length > 0);

  return (
    <Box>
      <FeedbackHeader employee={employee} providerType={providerType} />
      <RatingComponent
        selectedAnswerOptions={selectedAnswerOptions}
        answerType={answerType}
        answerOptions={answerOptions}
      />
      <FeedbackComponent feedbackText={feedbackText} feedbackTextHtml={feedbackTextHtml} answerType={answerType} />
      {!someDataPresent && <NoFeedbackComponent />}
      {!isLastItem && <Separator />}
    </Box>
  );
};

const PeerFeedbackComp = ({ reviewId, employeeId }) => {
  const dispatch = useDispatch();
  const {
    state: { peerFeedback },
  } = useReview();
  const { goalId, competencyId, questionId, answerOptions, answerType } = peerFeedback;

  const [loading, setLoading] = useState(false);
  const [peerFeedbacks, setPeerFeedbacks] = useState(null);

  const fetchData = () => {
    setLoading(true);
    getPeerFeedback({
      goalId,
      competencyId,
      reviewId,
      employeeId,
      questionId,
    })
      .then((feedbacks) => {
        setPeerFeedbacks(feedbacks);
      })
      .catch((err) => {
        dispatch(showErrorSnackbar(err.message || ERROR_MSG));
        console.log(err);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const showLoader = useCallback(
    () => (
      <DisplayInfo>
        <SkeletonLoader skeletons={['90%', '90%']} />
      </DisplayInfo>
    ),
    []
  );

  const showNoData = useCallback(
    () => (
      <DisplayInfo>
        <StyledTypography variant="body1">{NO_DATA_MSG}</StyledTypography>
      </DisplayInfo>
    ),
    []
  );

  const showErrorMessage = useCallback(
    () => (
      <DisplayInfo>
        <StyledTypography variant="body1" color="red">
          {ERROR_MSG}
        </StyledTypography>
      </DisplayInfo>
    ),
    []
  );

  useEffect(() => {
    if (questionId) fetchData();
  }, [questionId, goalId, competencyId]);

  return (
    <>
      {questionId === null && <PlaceHolderComp />}
      {questionId !== null && loading && showLoader()}
      {questionId !== null && !loading && peerFeedbacks === null && showErrorMessage()}
      {questionId !== null &&
        !loading &&
        peerFeedbacks !== null &&
        (peerFeedbacks.length === 0 ? (
          showNoData()
        ) : (
          <Box padding="1.5rem">
            {peerFeedbacks.map(
              ({ employee, feedbackText, feedbackTextHtml, selectedAnswerOptions, providerType }, index) => (
                <PeerFeedbackDisplayCard
                  employee={employee}
                  feedbackText={feedbackText}
                  feedbackTextHtml={feedbackTextHtml}
                  selectedAnswerOptions={selectedAnswerOptions}
                  answerType={answerType}
                  providerType={providerType}
                  isLastItem={index === peerFeedbacks.length - 1}
                  answerOptions={answerOptions}
                />
              )
            )}
          </Box>
        ))}
    </>
  );
};

export default PeerFeedbackComp;
